class ImageWithTextSlideshow {
  constructor(element) {
    this.element = element;
    this.swiper = null;
    this.autoplayObserver = null;
    this.slideCount = 0;
    this.mountSlider();
    this.bindThemeEditorEvents();
  }

  bindThemeEditorEvents() {
    window.addEventListener('shopify:block:select', (event) => {
      if (!this.element.contains(event.target)) return;
      const slide = event.target.closest('[data-index]');
      if (!slide || !this.swiper) return;
      const index = parseInt(slide.dataset.index, 10) - 1;
      if (this.swiper.params.loop) {
        this.swiper.slideToLoop(index, 600);
      } else {
        this.swiper.slideTo(index, 600);
      }
    });
  }

  mountSlider() {
    if (this.swiper) return;

    this.slideCount = this.element.querySelectorAll('.swiper-slide').length;
    if (this.slideCount < 2) return;

    if (typeof Swiper === 'undefined') {
      window.addEventListener('load', () => this.createSwiper(), { once: true });
      return;
    }

    this.createSwiper();
  }

  createSwiper() {
    if (this.swiper || typeof Swiper === 'undefined') return;

    const speed = this.element.dataset.speed ? parseInt(this.element.dataset.speed, 10) : 800;
    const interval = this.element.dataset.interval ? parseInt(this.element.dataset.interval, 10) : 4000;
    const autoplayEnabled = this.element.dataset.autoplay === 'true';
    const effect = this.element.dataset.effect || 'fade';
    const paginationEl = this.element.querySelector('.swiper-pagination');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const swiperConfig = {
      spaceBetween: 0,
      loop: this.slideCount >= 3,
      rewind: true,
      effect,
      speed,
      autoHeight: true,
      watchOverflow: true,
      observer: true,
      observeParents: true,
      simulateTouch: false,
      preventClicks: false,
      preventClicksPropagation: false,
      fadeEffect: {
        crossFade: true,
      },
      pagination: paginationEl ? { el: paginationEl, clickable: true } : undefined,
    };

    if (autoplayEnabled && !reducedMotion) {
      swiperConfig.autoplay = {
        delay: interval,
        disableOnInteraction: false,
        pauseOnMouseEnter: true,
        waitForTransition: false,
      };
    }

    this.swiper = new Swiper(this.element, swiperConfig);
    this.element.classList.add('iwts-swiper-ready');

    let refreshTimer;
    const scheduleLayoutRefresh = () => {
      clearTimeout(refreshTimer);
      refreshTimer = setTimeout(() => {
        if (!this.swiper) return;
        this.swiper.update();
        this.swiper.updateAutoHeight(0);
      }, 80);
    };

    requestAnimationFrame(scheduleLayoutRefresh);

    if (autoplayEnabled && !reducedMotion && this.swiper.autoplay) {
      this.bindAutoplayVisibility();
    }
  }

  bindAutoplayVisibility() {
    const section = this.element.closest('.section-image-with-text-slideshow');
    if (!section || !this.swiper?.autoplay) return;

    const syncAutoplay = (isVisible) => {
      if (!this.swiper?.autoplay) return;
      if (isVisible) {
        this.swiper.autoplay.start();
      } else {
        this.swiper.autoplay.stop();
      }
    };

    this.autoplayObserver = new IntersectionObserver(
      (entries) => syncAutoplay(Boolean(entries[0]?.isIntersecting)),
      { threshold: 0.2 }
    );

    this.autoplayObserver.observe(section);

    const rect = section.getBoundingClientRect();
    const vh = window.innerHeight || document.documentElement.clientHeight;
    syncAutoplay(rect.top < vh * 0.8 && rect.bottom > vh * 0.2);
  }

  destroy() {
    this.autoplayObserver?.disconnect();
    this.autoplayObserver = null;
    if (this.swiper) {
      this.swiper.destroy(true, true);
      this.swiper = null;
    }
    this.element.classList.remove('iwts-swiper-ready');
  }
}

function initImageWithTextSlideshows(root = document) {
  root.querySelectorAll('.iwt-slideshow-swiper:not([data-iwts-mounted])').forEach((element) => {
    element.dataset.iwtsMounted = 'true';
    element._iwtsInstance = new ImageWithTextSlideshow(element);
  });

  if (typeof window.themeScrollRevealInit === 'function') {
    window.themeScrollRevealInit(root);
  }
}

function destroyImageWithTextSlideshows(root) {
  root.querySelectorAll('.iwt-slideshow-swiper[data-iwts-mounted]').forEach((element) => {
    element._iwtsInstance?.destroy();
    delete element.dataset.iwtsMounted;
    delete element._iwtsInstance;
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initImageWithTextSlideshows());
} else {
  initImageWithTextSlideshows();
}

document.addEventListener('shopify:section:load', (event) => {
  initImageWithTextSlideshows(event.target);
});

document.addEventListener('shopify:section:unload', (event) => {
  destroyImageWithTextSlideshows(event.target);
});
